/**
 * Rastro do veículo: por onde ele passou, acumulado ao vivo.
 *
 * Os pontos vêm do `WS /stream` conforme chegam — não do histórico. É o rastro
 * do que aconteceu **enquanto o painel esteve aberto**, e é assim de propósito:
 * o operador está acompanhando agora, não investigando ontem.
 */
import { getVehicleStatus, type VehicleStatus } from "./fleet.ts";
import type { Vehicle } from "./schemas.ts";

/** Quantos pontos o rastro guarda. Passou disso, o mais antigo sai. */
export const TRAIL_MAX_POINTS = 10;

/**
 * Movimento mínimo para virar ponto novo, em metros.
 *
 * Sem isto o rastro enche de ruído: um ônibus **parado** oscila alguns metros no
 * GPS, e em cinco minutos os 10 pontos seriam deriva em cima do mesmo lugar —
 * apagando o trajeto de verdade que estava ali. 15 m é maior que a deriva típica
 * e menor que um quarteirão.
 */
export const TRAIL_MIN_METERS = 15;

export interface TrailPoint {
  readonly lat: number;
  readonly lon: number;
  /** Hora do dado (relógio do equipamento), para ordenar e para o tooltip. */
  readonly at: string | null;
  /**
   * Estado do veículo NAQUELE momento — não o de agora.
   *
   * É o que faz o rastro contar a história: o trecho em que ele andou fica
   * verde, o trecho em que ficou parado com o motor ligado fica azul, e o
   * desligado fica cinza. Pintar tudo com o estado atual apagaria exatamente a
   * informação que o rastro existe para dar.
   */
  readonly status: VehicleStatus;
}

/** Distância aproximada em metros. Equirretangular basta nesta escala. */
export function distanceMeters(a: TrailPoint, b: TrailPoint): number {
  const RADIUS = 6_371_000;
  const toRad = Math.PI / 180;
  const x = (b.lon - a.lon) * toRad * Math.cos(((a.lat + b.lat) / 2) * toRad);
  const y = (b.lat - a.lat) * toRad;
  return Math.hypot(x, y) * RADIUS;
}

/**
 * Acrescenta o ponto ao rastro, se ele valer a pena.
 *
 * Devolve o array ANTERIOR quando nada muda — a identidade preservada é o que
 * impede o React de redesenhar o mapa a cada mensagem do stream, e mensagem
 * chega mesmo quando só a velocidade mudou.
 */
export function appendTrailPoint(
  trail: readonly TrailPoint[],
  point: TrailPoint,
  maxPoints = TRAIL_MAX_POINTS,
): readonly TrailPoint[] {
  const last = trail[trail.length - 1];
  if (last && distanceMeters(last, point) < TRAIL_MIN_METERS) return trail;

  const next = [...trail, point];
  // `slice` a partir do fim: o mais antigo é o que sai.
  return next.length > maxPoints ? next.slice(next.length - maxPoints) : next;
}

/** O ponto atual do veículo, quando ele tem posição. */
export function toTrailPoint(vehicle: Vehicle): TrailPoint | null {
  if (vehicle.lat == null || vehicle.lon == null) return null;
  // O estado é capturado AQUI, no instante em que o ponto entra — a mesma
  // função que colore o marcador e a lista, para as três não discordarem.
  return {
    lat: vehicle.lat,
    lon: vehicle.lon,
    at: vehicle.deviceClock,
    status: getVehicleStatus(vehicle),
  };
}
